import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/types'

import Link from 'next/link'
import { Clock, Mail, MapPin, Phone } from 'lucide-react'
import { Crest } from '@/components/generals/crest'

import {
    footerKeyLinks,
    footerQuickLinks,
    legalLinks,
    routes
} from '@/constants/navigation'
import { school } from '@/constants/site'

interface FooterLinkListProps {
    heading: string
    links: NavLink[]
}

const footerLinkClass = 'inline-flex min-h-9 items-center rounded-sm text-white/75 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'

const FooterLinkList: FC<FooterLinkListProps> = ({ heading, links }): ReactNode => {
    return (
        <nav aria-label={heading}>
            <h2 className='font-heading text-lg font-semibold text-white'>
                {heading}
            </h2>

            <ul className='mt-4 grid gap-0.5 text-[0.95rem]'>
                {links.map((link) => (
                    <li key={link.href}>
                        <Link href={link.href} className={footerLinkClass}>
                            {link.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    )
}

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-navy-deep text-white/75'>
            <div className='h-1.5 bg-gold' aria-hidden='true' />

            <div className='mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-6 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-4'>
                    <Link
                        href={routes.home}
                        className='inline-flex items-center gap-3 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                    >
                        <Crest className='h-14 w-auto' />

                        <span className='flex flex-col'>
                            <span className='font-heading text-2xl font-semibold text-white'>
                                {school.name}
                            </span>

                            <span className='text-xs font-semibold uppercase tracking-[0.18em] text-gold'>
                                {school.motto}
                            </span>
                        </span>
                    </Link>

                    <address className='mt-8 grid gap-3 not-italic'>
                        <p className='flex gap-3'>
                            <MapPin aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-gold' />
                            <span>
                                {school.address.street}, {school.address.town} {school.address.postcode}
                            </span>
                        </p>

                        <p className='flex gap-3'>
                            <Phone aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-gold' />
                            <span>
                                Reception: <a href={school.phone.href} className='text-white underline-offset-4 hover:underline'>{school.phone.display}</a>
                                <br />
                                Absence line: <a href={school.absenceLine.href} className='text-white underline-offset-4 hover:underline'>{school.absenceLine.display}</a>
                            </span>
                        </p>

                        <p className='flex gap-3'>
                            <Mail aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-gold' />
                            <a href={`mailto:${school.email}`} className='break-all text-white underline-offset-4 hover:underline'>
                                {school.email}
                            </a>
                        </p>
                    </address>

                    <p className='mt-6 text-sm leading-relaxed'>
                        Questions from parents and the public are handled by {school.people.enquiries}, Office Manager.
                    </p>
                </div>

                <div className='lg:col-span-3'>
                    <h2 className='font-heading text-lg font-semibold text-white'>
                        Key contacts
                    </h2>

                    <dl className='mt-4 grid gap-4 text-[0.95rem]'>
                        <div>
                            <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-gold'>Headteacher</dt>
                            <dd className='mt-1 text-white'>{school.people.headteacher}</dd>
                        </div>

                        <div>
                            <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-gold'>Chair of Governors</dt>
                            <dd className='mt-1 text-white'>{school.people.chairOfGovernors}</dd>
                        </div>

                        <div>
                            <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-gold'>SENCo</dt>
                            <dd className='mt-1 text-white'>
                                {school.people.senco}
                                <br />
                                <a href={`mailto:${school.sencoEmail}`} className='break-all text-white/75 underline-offset-4 hover:text-white hover:underline'>
                                    {school.sencoEmail}
                                </a>
                            </dd>
                        </div>

                        <div>
                            <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-gold'>Designated Safeguarding Lead</dt>
                            <dd className='mt-1 text-white'>{school.people.designatedSafeguardingLead}</dd>
                        </div>
                    </dl>
                </div>

                <div className='grid gap-12 sm:grid-cols-2 lg:col-span-5 lg:gap-10'>
                    <FooterLinkList heading='Quick links' links={footerQuickLinks} />
                    <FooterLinkList heading='Key information' links={footerKeyLinks} />

                    <div className='rounded-2xl border border-white/10 bg-white/5 p-5 sm:col-span-2'>
                        <h2 className='flex items-center gap-2 font-heading text-lg font-semibold text-white'>
                            <Clock aria-hidden='true' className='size-5 text-gold' />
                            The school day
                        </h2>

                        <p className='mt-2 text-[0.95rem] leading-relaxed'>
                            Monday to Friday, {school.schoolDay.start} to {school.schoolDay.end}. That is {school.schoolDay.weeklyHours} a week, including breaks.
                            <br />
                            Reception is open {school.officeHours}
                        </p>
                    </div>
                </div>
            </div>

            <div className='border-t border-white/10'>
                <div className='mx-auto flex max-w-7xl flex-col gap-4 px-5 py-6 text-sm sm:px-6 lg:flex-row lg:items-center lg:justify-between'>
                    <p className='max-w-3xl leading-relaxed text-white/60'>
                        <span suppressHydrationWarning>&copy; {new Date().getFullYear()}</span> {school.name}. {school.name} is part of {school.trust}, a charitable company limited by guarantee registered in England and Wales.
                    </p>

                    <ul className='flex flex-wrap gap-x-6 gap-y-1'>
                        {legalLinks.map((link) => (
                            <li key={link.href}>
                                <Link href={link.href} className={footerLinkClass}>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </footer>
    )
}
